/**
 * Build forecast visual structure.
 *
 * @module forecast.builder
 */

'use strict';

var app     = require('mag-app'),
    convert = require('./utils/converter'),
    doc     = document,
    builder = {},
    $msgElem;


/**
 * Builder initialization.
 *
 * @param {Object} elem - parent container for forecasts
 * @param {Object} data - saved location
 *
 * @return {Object} parent container for forecasts
 */
builder.init = function ( elem, data ) {
    return data ? this.setForecast(elem, data) : this.setNoData(elem);
};


/**
 * Set message on empty locations list.
 *
 * @param {Object} elem - element as a container for message block
 *
 * @return {Object} initial element with message block inside
 */
builder.setNoData = function ( elem ) {
    $msgElem = doc.createElement('div');

    $msgElem.className = 'addCityIcon';
    $msgElem.innerText = gettext('Press');
    $msgElem.appendChild($msgElem.cloneNode(false));
    $msgElem.lastElementChild.className = 'theme-icon theme-icon-rc-f2';
    $msgElem.appendChild(doc.createTextNode(gettext('to add a new city')));
    doc.getElementById('forecast').classList.add('hiddenBlock');
    elem.$body.appendChild($msgElem);

    return elem;
};


/**
 * Build DOM structure and construct map of active elements.
 *
 * @param {Object} elem - parent for DOM structure
 *
 * @private
 *
 * @return {Object} map of active elements
 */
function buildDOMStruct ( elem ) {
    var resolution = screen.height,
        imgPath    = 'img/' + resolution + '/',
        struct;

    if ( !doc.getElementById('forecast').appended ) {
        elem.$body.appendChild(doc.getElementById('forecast'));
        doc.getElementById('forecast').appended = true;
    }

    doc.getElementById('forecast').classList.remove('hiddenBlock');

    if ( $msgElem ) {
        $msgElem.classList.add('hiddenBlock');
    }

    struct = {
        currentDayDate:             doc.getElementById('currentDayDate'),
        currentDayTemp:             doc.getElementById('currentDayTemp'),
        currentDayIcon:             doc.getElementById('currentDayIcon'),
        currentDayDesc:             doc.getElementById('currentDayDesc'),
        detailsSunnyIco:            doc.getElementById('detailsSunnyIco'),
        currentDayDetailsDayTemp:   doc.getElementById('currentDayDetailsDayTemp'),
        detailsNightIco:            doc.getElementById('detailsNightIco'),
        currentDayDetailsNightTemp: doc.getElementById('currentDayDetailsNightTemp'),
        detailsHumidityIco:         doc.getElementById('detailsHumidityIco'),
        currentDayDetailsHumidity:  doc.getElementById('currentDayDetailsHumidity'),
        detailsWindIco:             doc.getElementById('detailsWindIco'),
        currentDayDetailsWind:      doc.getElementById('currentDayDetailsWind'),
        otherDaysFirstDayDate:      doc.getElementById('otherDaysFirstDayDate'),
        otherDaysFirstDayIcon:      doc.getElementById('otherDaysFirstDayIcon'),
        otherDaysFirstDayTemp:      doc.getElementById('otherDaysFirstDayTemp'),
        otherDaysSecondDayDate:     doc.getElementById('otherDaysSecondDayDate'),
        otherDaysSecondDayIcon:     doc.getElementById('otherDaysSecondDayIcon'),
        otherDaysSecondDayTemp:     doc.getElementById('otherDaysSecondDayTemp'),
        otherDaysThirdDayDate:      doc.getElementById('otherDaysThirdDayDate'),
        otherDaysThirdDayIcon:      doc.getElementById('otherDaysThirdDayIcon'),
        otherDaysThirdDayTemp:      doc.getElementById('otherDaysThirdDayTemp')
    };

    // fixed icons
    struct.detailsSunnyIco.src    = imgPath + 'sunny.ico.png';
    struct.detailsNightIco.src    = imgPath + 'night.ico.png';
    struct.detailsHumidityIco.src = imgPath + 'humidity.ico.png';
    struct.detailsWindIco.src     = imgPath + 'wind.ico.png';

    // placeholders
    struct.currentDayIcon.src         = imgPath + 'dummy.active.png';
    struct.otherDaysFirstDayIcon.src  = imgPath + 'dummy.normal.png';
    struct.otherDaysSecondDayIcon.src = imgPath + 'dummy.normal.png';
    struct.otherDaysThirdDayIcon.src  = imgPath + 'dummy.normal.png';

    if ( resolution === 480 || resolution === 576 ) {
        [].forEach.call([].slice.call(doc.getElementsByClassName('otherDaysTempIco')), function ( element ) {
            element.classList.remove('hidden');
        });
    }

    return struct;
}


/**
 * Build forecast structure and init forecast block with new data.
 *
 * @param {Object} elem - container of slots
 * @param {Object[]} data - structured forecast
 */
builder.setForecast = function ( elem, data ) {
    var struct = elem.DOMStruct,
        evalTemp, evalWind, removeZeros, currentDay;

    if ( elem.isDOMStructReady && data ) {
        evalTemp = function ( temp ) {
            var tmp = app.userStore.settings.temperature === 'F' ? convert(temp, 'CF') : temp;

            // and replace with true minus
            return (['', '+'][+(parseInt(tmp, 10) > 0)] + (app.userStore.settings.temperature === 'F' ? tmp : tmp + '\u00B0C')).replace('-', '−');
        };
        evalWind = function ( wind ) {
            var units = app.userStore.settings.windSpeedUnits;

            if ( units === 'km/h' || units === 'm/h' ) {
                return units === 'km/h' ? convert(wind, 'MSKH').replace(/([\d.]+)/, '$1 ') : convert(wind, 'MSMH').replace(/([\d.]+)/, '$1 ');
            }

            return wind + ' ' + gettext('m/s');
        };
        removeZeros = function ( dateString ) {
            return dateString.replace(/0?(\d+)/, '$1');
        };
        currentDay = data.current;

        struct.currentDayDate.innerText             = currentDay.dayName + ', ' + removeZeros(currentDay.date);
        struct.currentDayTemp.innerText             = evalTemp(currentDay.temperature);
        struct.currentDayIcon.src                   = currentDay.indicator;
        struct.currentDayDesc.innerText             = currentDay.description;
        struct.currentDayDetailsDayTemp.innerText   = evalTemp(currentDay.temperatureDay);
        struct.currentDayDetailsNightTemp.innerText = evalTemp(currentDay.temperatureNight);
        struct.currentDayDetailsHumidity.innerText  = currentDay.humidity;
        struct.currentDayDetailsWind.innerText      = currentDay.windDirection + ' ' + evalWind(currentDay.windSpeed);

        struct.otherDaysFirstDayDate.children[0].innerText  = data.days[0].dayName;
        struct.otherDaysFirstDayDate.children[1].innerText  = removeZeros(data.days[0].date);
        struct.otherDaysFirstDayIcon.src                    = data.days[0].indicator;
        struct.otherDaysFirstDayTemp.children[1].innerText  = evalTemp(data.days[0].temperatureDay);
        struct.otherDaysFirstDayTemp.children[3].innerText  = evalTemp(data.days[0].temperatureNight);

        struct.otherDaysSecondDayDate.children[0].innerText = data.days[1].dayName;
        struct.otherDaysSecondDayDate.children[1].innerText = removeZeros(data.days[1].date);
        struct.otherDaysSecondDayIcon.src                   = data.days[1].indicator;
        struct.otherDaysSecondDayTemp.children[1].innerText = evalTemp(data.days[1].temperatureDay);
        struct.otherDaysSecondDayTemp.children[3].innerText = evalTemp(data.days[1].temperatureNight);

        struct.otherDaysThirdDayDate.children[0].innerText  = data.days[2].dayName;
        struct.otherDaysThirdDayDate.children[1].innerText  = removeZeros(data.days[2].date);
        struct.otherDaysThirdDayIcon.src                    = data.days[2].indicator;
        struct.otherDaysThirdDayTemp.children[1].innerText  = evalTemp(data.days[2].temperatureDay);
        struct.otherDaysThirdDayTemp.children[3].innerText  = evalTemp(data.days[2].temperatureNight);
    } else {
        elem.DOMStruct = buildDOMStruct(elem);
        elem.isDOMStructReady = true;
    }
};


module.exports = builder;
